import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import ClientApplicationGaps from '../../../components/admin/applications/ClientApplicationGaps';

const STATUS_OPTIONS = ['draft', 'submitted', 'review', 'offer', 'accepted', 'rejected'];
const STATUS_COLORS = {
  draft: 'bg-gray-600 text-gray-200 border-gray-500',
  submitted: 'bg-blue-900/40 text-blue-300 border-blue-700',
  review: 'bg-yellow-900/40 text-yellow-300 border-yellow-700',
  offer: 'bg-purple-900/40 text-purple-300 border-purple-700',
  accepted: 'bg-green-900/40 text-green-300 border-green-700',
  rejected: 'bg-red-900/40 text-red-300 border-red-700'
};

// Helper: Calculate days until deadline
const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const deadline = new Date(dateStr);
  const now = new Date();
  return Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
};

// Helper: Get urgency indicator
const getUrgencyBadge = (days) => {
  if (days === null) return null;
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, color: 'bg-red-600 text-white' };
  if (days <= 3) return { label: `${days}d left`, color: 'bg-red-900/60 text-red-300' };
  if (days <= 7) return { label: `${days}d left`, color: 'bg-yellow-900/60 text-yellow-300' };
  if (days <= 14) return { label: `${days}d left`, color: 'bg-blue-900/40 text-blue-300' };
  return { label: `${days}d`, color: 'bg-gray-700 text-gray-300' };
};

// Analytics Card Component
const StatCard = ({ label, value, icon, color = 'blue' }) => (
  <div className={`bg-gray-800 border border-gray-700 rounded-lg p-4`}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-xs text-gray-400 uppercase tracking-wider">{label}</div>
      </div>
      <div className={`text-3xl opacity-60`}>{icon}</div>
    </div>
  </div>
);

const UniversityApplicationsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);

  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    university: 'all',
    country: 'all',
    intake: 'all',
    checklistStatus: 'all', // all | incomplete | complete
    urgency: 'all' // all | overdue | urgent | normal
  });
  const [search, setSearch] = useState('');

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ field: 'updated_at', direction: 'desc' });

  // Filter options (populated from data)
  const [universities, setUniversities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [intakes, setIntakes] = useState([]);

  const showToast = (type, text) => {
    setToast({ type, text });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      // Enhanced query with more data
      const { data, error: fetchError } = await supabase
        .from('uni_applications')
        .select(`
          *,
          clients (id, first_name, last_name, primary_email),
          programs (
            id, name, discipline, level,
            university: universities (id, name, country)
          ),
          intakes (id, term, apply_close),
          uni_app_documents (id, status)
        `)
        .order('updated_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Process rows to add computed fields
      const processedRows = (data || []).map(row => {
        const deadline = row.intakes?.apply_close || row.metadata?.deadline;
        const daysUntil = getDaysUntil(deadline);
        const docs = row.uni_app_documents || [];
        const completedDocs = docs.filter(d => ['uploaded', 'submitted', 'approved'].includes(d.status)).length;
        const checklistProgress = docs.length > 0 ? Math.round((completedDocs / docs.length) * 100) : 0;

        return {
          ...row,
          _deadline: deadline,
          _daysUntil: daysUntil,
          _checklistProgress: checklistProgress,
          _checklistCount: docs.length,
          _completedCount: completedDocs,
          _country: row.programs?.university?.country || row.metadata?.country || null,
          _universityName: row.programs?.university?.name || row.metadata?.university || '-',
          _programName: row.programs?.name || row.metadata?.program_text || '-',
          _clientName: `${row.clients?.first_name || ''} ${row.clients?.last_name || ''}`.trim() || '-'
        };
      });

      setRows(processedRows);

      // Extract filter options
      const uniSet = new Set();
      const countrySet = new Set();
      const intakeSet = new Set();

      processedRows.forEach(r => {
        if (r._universityName && r._universityName !== '-') uniSet.add(r._universityName);
        if (r._country) countrySet.add(r._country);
        if (r.intakes?.term) intakeSet.add(r.intakes.term);
      });

      setUniversities([...uniSet].sort());
      setCountries([...countrySet].sort());
      setIntakes([...intakeSet].sort());

    } catch (e) {
      console.error('Failed to load applications:', e);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Filter and sort rows
  const filteredAndSorted = useMemo(() => {
    let result = [...rows];

    // Apply filters
    if (filters.status !== 'all') {
      result = result.filter(r => r.status === filters.status);
    }
    if (filters.university !== 'all') {
      result = result.filter(r => r._universityName === filters.university);
    }
    if (filters.country !== 'all') {
      result = result.filter(r => r._country === filters.country);
    }
    if (filters.intake !== 'all') {
      result = result.filter(r => r.intakes?.term === filters.intake);
    }
    if (filters.checklistStatus !== 'all') {
      if (filters.checklistStatus === 'complete') {
        result = result.filter(r => r._checklistProgress === 100);
      } else if (filters.checklistStatus === 'incomplete') {
        result = result.filter(r => r._checklistProgress < 100);
      }
    }
    if (filters.urgency !== 'all') {
      if (filters.urgency === 'overdue') {
        result = result.filter(r => r._daysUntil !== null && r._daysUntil < 0);
      } else if (filters.urgency === 'urgent') {
        result = result.filter(r => r._daysUntil !== null && r._daysUntil >= 0 && r._daysUntil <= 7);
      } else if (filters.urgency === 'normal') {
        result = result.filter(r => r._daysUntil === null || r._daysUntil > 7);
      }
    }

    // Apply search
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(r => {
        const hay = `${r._clientName} ${r._universityName} ${r._programName} ${r.status || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.field) {
        case 'client':
          aVal = a._clientName?.toLowerCase() || '';
          bVal = b._clientName?.toLowerCase() || '';
          break;
        case 'university':
          aVal = a._universityName?.toLowerCase() || '';
          bVal = b._universityName?.toLowerCase() || '';
          break;
        case 'status':
          aVal = STATUS_OPTIONS.indexOf(a.status);
          bVal = STATUS_OPTIONS.indexOf(b.status);
          break;
        case 'deadline':
          aVal = a._deadline ? new Date(a._deadline).getTime() : Infinity;
          bVal = b._deadline ? new Date(b._deadline).getTime() : Infinity;
          break;
        case 'checklist':
          aVal = a._checklistProgress;
          bVal = b._checklistProgress;
          break;
        case 'updated_at':
        default:
          aVal = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bVal = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [rows, filters, search, sortConfig]);

  // Analytics computations
  const analytics = useMemo(() => {
    const total = rows.length;
    const byStatus = {};
    STATUS_OPTIONS.forEach(s => byStatus[s] = 0);
    rows.forEach(r => {
      if (byStatus[r.status] !== undefined) byStatus[r.status]++;
    });
    const accepted = byStatus.accepted || 0;
    const offers = byStatus.offer || 0;
    const rejected = byStatus.rejected || 0;
    const decided = accepted + offers + rejected;
    const acceptanceRate = decided > 0 ? Math.round(((accepted + offers) / decided) * 100) : 0;
    const overdue = rows.filter(r => r._daysUntil !== null && r._daysUntil < 0).length;
    const urgent = rows.filter(r => r._daysUntil !== null && r._daysUntil >= 0 && r._daysUntil <= 7).length;

    return { total, byStatus, acceptanceRate, overdue, urgent };
  }, [rows]);

  // Sortable column header
  const SortableHeader = ({ field, children, className = '' }) => (
    <th
      className={`bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => setSortConfig(prev => ({
        field,
        direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
      }))}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortConfig.field === field && (
          <span className="text-blue-400">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  const updateStatus = async (id, newStatus) => {
    const { error } = await supabase.from('uni_applications').update({ status: newStatus }).eq('id', id);
    if (error) {
      showToast('error', 'Failed to update status');
    } else {
      showToast('success', 'Status updated');
      load();
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      university: 'all',
      country: 'all',
      intake: 'all',
      checklistStatus: 'all',
      urgency: 'all'
    });
    setSearch('');
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length + (search ? 1 : 0);

  if (loading) return (
    <div className="min-h-screen bg-gray-900 font-mono text-white flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
      <div className="text-gray-400">Loading university applications...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">🎓 University Applications</h1>
          <p className="text-gray-400">Track and manage university applications across all programs</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/univ_apps/new"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium text-sm transition-colors"
          >
            + New Application
          </Link>
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm transition-colors"
          >
            {showAnalytics ? '📊 Hide Stats' : '📊 Show Stats'}
          </button>
          <button
            onClick={load}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm transition-colors"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatCard label="Total" value={analytics.total} icon="📋" />
          <StatCard label="Accepted/Offers" value={analytics.byStatus.accepted + analytics.byStatus.offer} icon="✅" />
          <StatCard label="In Review" value={analytics.byStatus.review + analytics.byStatus.submitted} icon="⏳" />
          <StatCard label="Success Rate" value={`${analytics.acceptanceRate}%`} icon="📈" />
          <StatCard label="Overdue" value={analytics.overdue} icon="🔴" />
          <StatCard label="Urgent (≤7d)" value={analytics.urgent} icon="⚠️" />
        </div>
      )}

      {/* Client Application Gaps - No Client Left Behind */}
      {showAnalytics && (
        <div className="mb-6">
          <ClientApplicationGaps />
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <input
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="🔍 Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="all">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              value={filters.university}
              onChange={(e) => setFilters(f => ({ ...f, university: e.target.value }))}
            >
              <option value="all">All Universities</option>
              {universities.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <select
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              value={filters.country}
              onChange={(e) => setFilters(f => ({ ...f, country: e.target.value }))}
            >
              <option value="all">All Countries</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              value={filters.intake}
              onChange={(e) => setFilters(f => ({ ...f, intake: e.target.value }))}
            >
              <option value="all">All Intakes</option>
              {intakes.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            <select
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              value={filters.urgency}
              onChange={(e) => setFilters(f => ({ ...f, urgency: e.target.value }))}
            >
              <option value="all">All Urgency</option>
              <option value="overdue">🔴 Overdue</option>
              <option value="urgent">⚠️ Urgent (≤7d)</option>
              <option value="normal">🟢 Normal</option>
            </select>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 bg-red-900/40 border border-red-700 text-red-300 rounded text-sm hover:bg-red-900/60 transition-colors"
              >
                ✕ Clear ({activeFilterCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-800 text-red-400 p-4 rounded mb-6">
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Results count */}
      <div className="text-gray-400 text-sm mb-3">
        Showing {filteredAndSorted.length} of {rows.length} applications
      </div>

      {/* Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <SortableHeader field="client">Client</SortableHeader>
                <SortableHeader field="university">University • Program</SortableHeader>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Country</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Intake</th>
                <SortableHeader field="deadline">Deadline</SortableHeader>
                <SortableHeader field="checklist">Checklist</SortableHeader>
                <SortableHeader field="status">Status</SortableHeader>
                <SortableHeader field="updated_at">Updated</SortableHeader>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredAndSorted.map(a => {
                const urgency = getUrgencyBadge(a._daysUntil);
                return (
                  <tr key={a.id} className="hover:bg-gray-700/50 transition-colors">
                    {/* Client */}
                    <td className="p-3 border-b border-gray-700">
                      <div className="font-semibold text-white">{a._clientName}</div>
                      {a.clients?.primary_email && (
                        <div className="text-xs text-gray-500 truncate max-w-[150px]">{a.clients.primary_email}</div>
                      )}
                    </td>
                    {/* University & Program */}
                    <td className="p-3 border-b border-gray-700">
                      <div className="text-white font-medium">{a._universityName}</div>
                      <div className="text-xs text-gray-400">{a._programName}</div>
                    </td>
                    {/* Country */}
                    <td className="p-3 border-b border-gray-700 text-gray-300">
                      {a._country || '-'}
                    </td>
                    {/* Intake */}
                    <td className="p-3 border-b border-gray-700 text-gray-300 text-sm">
                      {a.intakes?.term || '-'}
                    </td>
                    {/* Deadline */}
                    <td className="p-3 border-b border-gray-700">
                      {a._deadline ? (
                        <div>
                          <div className="text-gray-300 text-sm">{new Date(a._deadline).toLocaleDateString()}</div>
                          {urgency && (
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium mt-1 ${urgency.color}`}>
                              {urgency.label}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    {/* Checklist */}
                    <td className="p-3 border-b border-gray-700">
                      {a._checklistCount > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${a._checklistProgress === 100 ? 'bg-green-500' : a._checklistProgress >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${a._checklistProgress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400">{a._completedCount}/{a._checklistCount}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs">No items</span>
                      )}
                    </td>
                    {/* Status */}
                    <td className="p-3 border-b border-gray-700">
                      <select
                        className={`px-2 py-1 text-xs rounded border font-medium ${STATUS_COLORS[a.status] || 'bg-gray-700 text-white border-gray-600'}`}
                        value={a.status}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    {/* Updated */}
                    <td className="p-3 border-b border-gray-700 text-gray-400 text-sm">
                      {a.updated_at ? new Date(a.updated_at).toLocaleDateString() : '-'}
                    </td>
                    {/* Actions */}
                    <td className="p-3 border-b border-gray-700 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        {a.case_id && (
                          <Link
                            to={`/admin/cases/${a.case_id}`}
                            className="px-2 py-1 rounded text-xs font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600"
                          >
                            Case
                          </Link>
                        )}
                        <Link
                          to={`/admin/univ_apps/${a.id}`}
                          className="px-2 py-1 rounded text-xs font-medium bg-blue-900/40 text-blue-300 border border-blue-700 hover:bg-blue-900/60"
                        >
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredAndSorted.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-12 text-center text-gray-500">
                    {activeFilterCount > 0 ? 'No applications match your filters.' : 'No applications found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 p-4 rounded border ${toast.type === 'success' ? 'bg-green-900/20 border-green-800 text-green-400' : toast.type === 'error' ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-blue-900/20 border-blue-800 text-blue-400'}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default UniversityApplicationsPage;
